import {DotYouClient} from "@homebase-id/js-lib/core";
import {makeAutoObservable, runInAction} from "mobx";
import {ContactProgressionEvent, ContactProgressionEventType} from "../contact/ContactProgressionEventProvider";
import {ContactProvider} from "../contact/ContactProvider";
import {HealingDefinitionProvider} from "../definitions/HealingDefinitionProvider";
import {ClassDefinitionProvider} from "../definitions/ClassDefinitionProvider";
import {InitiationDefinitionProvider} from "../definitions/InitiationDefinitionProvider";
import {FullSpiritActivation, LifeActivation} from "../definitions/builtin/BuiltInHealings";
import {EmpowerThyself} from "../definitions/builtin/BuiltInClasses";


export interface Stat {
    definitionId: string;
    definitionType: string;
    title: string;
    totalEvents: number;
    uniqueContacts: number;
    firstEventUtc: number;
    lastEventUtc: number;
}

export type StatKey = "life-activation" | "adept-initiation" | "sg-1" | string;

export class StatsProvider {
    private static instance: StatsProvider
    private readonly dotYouClient: DotYouClient;
    public StateChanged: number = 0;

    constructor(dotYouClient: DotYouClient) {
        this.dotYouClient = dotYouClient;
        makeAutoObservable(this);
    }

    static getInstance(dotYouClient: DotYouClient) {
        if (StatsProvider.instance == null) {
            StatsProvider.instance = new StatsProvider(dotYouClient);
        }
        return StatsProvider.instance;
    }

    public async getLiveStats(days?: number): Promise<Stat[]> {
        const provider = ContactProvider.getInstance(this.dotYouClient).ProgressionEvents;
        const allEvents = await provider.getAllEvents({
            cassieQueryOptions: {
                forceServerCall: true
            }
        });

        const healings = HealingDefinitionProvider.getInstance(this.dotYouClient);
        const classes = ClassDefinitionProvider.getInstance(this.dotYouClient);
        const initiations = InitiationDefinitionProvider.getInstance(this.dotYouClient);

        const stats: Stat[] = [];

        // Compute cutoff if days is specified
        const cutoffUtcSeconds =
            days !== undefined
                ? Math.floor(Date.now() / 1000) - days * 24 * 60 * 60
                : undefined;

        for (const [type, defs] of Object.entries(allEvents) as [ContactProgressionEventType, Record<string, ContactProgressionEvent[]>][]) {
            for (const [definitionId, evs] of Object.entries(defs)) {
                // Apply filter by cutoff (if days provided)
                const filteredEvents = cutoffUtcSeconds
                    ? evs.filter(e => e.eventDateUtcSeconds >= cutoffUtcSeconds)
                    : evs;

                // Skip if no events remain
                if (filteredEvents.length === 0) continue;

                // Pick correct definition provider
                let title = "";
                if (type === "healing") {
                    const def = await healings.get(definitionId);
                    title = def?.title ?? definitionId;
                } else if (type === "class") {
                    const def = await classes.get(definitionId);
                    title = def?.title ?? definitionId;
                } else if (type === "initiation") {
                    const def = await initiations.get(definitionId);
                    title = def?.title ?? definitionId;
                }

                // Derive stats
                const contactIds = new Set(filteredEvents.map(e => e.contactId));
                const firstEventUtc = Math.min(...filteredEvents.map(e => e.eventDateUtcSeconds));
                const lastEventUtc = Math.max(...filteredEvents.map(e => e.eventDateUtcSeconds));

                stats.push({
                    definitionId,
                    definitionType: type,
                    title,
                    totalEvents: filteredEvents.length,
                    uniqueContacts: contactIds.size,
                    firstEventUtc,
                    lastEventUtc,
                });
            }
        }

        // Ensure required definitions exist
        const requiredDefs: { id: string; type: ContactProgressionEventType }[] = [
            {id: LifeActivation.id, type: "healing"},
            {id: FullSpiritActivation.id, type: "healing"},
            {id: EmpowerThyself.id, type: "class"},
        ];

        for (const req of requiredDefs) {
            if (!stats.find(s => s.definitionId === req.id)) {
                // Get title if possible
                let title = req.id;
                if (req.type === "healing") {
                    const def = await healings.get(req.id);
                    title = def?.title ?? req.id;
                } else if (req.type === "class") {
                    const def = await classes.get(req.id);
                    title = def?.title ?? req.id;
                } else if (req.type === "initiation") {
                    const def = await initiations.get(req.id);
                    title = def?.title ?? req.id;
                }

                stats.push({
                    definitionId: req.id,
                    definitionType: req.type,
                    title,
                    totalEvents: 0,
                    uniqueContacts: 0,
                    firstEventUtc: 0,
                    lastEventUtc: 0,
                });
            }
        }

        // Reorder: requiredDefs always first, in given order
        const requiredOrder = new Map(requiredDefs.map((r, i) => [r.id, i]));
        stats.sort((a, b) => {
            const aIdx = requiredOrder.has(a.definitionId) ? requiredOrder.get(a.definitionId)! : Infinity;
            const bIdx = requiredOrder.has(b.definitionId) ? requiredOrder.get(b.definitionId)! : Infinity;
            return aIdx - bIdx;
        });

        return stats;
    }

    private notifyStateChange() {
        runInAction(() => {
            this.StateChanged++;
        });
    }

}